import * as React from 'react';
import camelCase from 'lodash/camelCase';

import BarChartProgress from 'components/graphs/bar-chart-progress/BarChartProgress';

import { __t, applyTranslations } from 'utils/language';
import { COLORS, getAvailableChannels } from 'utils/reporting';
import { getMetricTypeWidth } from 'modules/reporting/utils/reporting-task-types';

import './task-progress-widget.scss';

applyTranslations(require('./task-progress-widget.i18n.json'));

import type { BarChartProgressItem } from 'types';
import type { ReportingDeliverableTypes } from 'modules/reporting/types/ReportingMetaInfo.type';
import type { ReportingKPIData } from 'modules/reporting/types';

type ReportingKPITasksProgress = {
  instagramPost: ReportingKPIData | undefined | null,
  instagramStory: ReportingKPIData | undefined | null,
  tiktokVideo: ReportingKPIData | undefined | null,
  zalandoOutfit: ReportingKPIData | undefined | null
};

type Props = {
  tasksProgress: ReportingKPITasksProgress,
  availableTaskTypes: Array<ReportingDeliverableTypes>,
  isLoading: boolean
};

const TaskOrder: Record<ReportingDeliverableTypes, number> = {
  instagram_post: 0,
  instagram_story: 1,
  instagram_reel: 2,
  tiktok_video: 3,
  youtube_video: 4,
  zalando_outfit: 5
};

const getBarChartData = (
  tasksProgress: ReportingKPITasksProgress,
  availableTaskTypes: Array<ReportingDeliverableTypes>
): Array<BarChartProgressItem> => availableTaskTypes.map(type => {
  const actual = tasksProgress[camelCase(type)]?.actual || 0;
  const agreed = tasksProgress[camelCase(type)]?.agreed || 0;

  return ({
    key: type,
    label: __t(`task_progress_widget.types.${type}`),
    metric: `${actual}/${agreed}`,
    actual: {
      percentage: actual > agreed ? 100 : Math.round(actual * 100 / agreed),
      metric: actual
    },
    estimated: {
      percentage: 100,
      metric: agreed
    },
    bgColor: COLORS[type].main
  });
});

const TaskProgressWidget = ({
  availableTaskTypes,
  tasksProgress,
  isLoading
}: Props) => {
  const availableChannels = getAvailableChannels(availableTaskTypes);

  const barChartItems = getBarChartData(tasksProgress, availableTaskTypes)
    .sort((a, b) => {
      const orderA = TaskOrder[a.key];
      const orderB = TaskOrder[b.key];

      if (orderA === orderB) {
        return 0;
      }

      return (orderA > orderB) ? 1 : -1;
    });

  const containerStyles = availableTaskTypes.length === 2 && availableChannels.length === 2 ? {
    minHeight: 60
  } : { marginBottom: 16 };

  return (
    <div className="task-progress-widget">
      <BarChartProgress
        data={barChartItems}
        title={__t('task_progress_widget.title')}
        isLoading={isLoading}
        emptyMessage={__t('task_progress_widget.no_content')}
        barStyle={{
          title: { marginBottom: 16 },
          container: containerStyles
        }}
        metricContainerType={getMetricTypeWidth(barChartItems)}
      />
    </div>
  );
};


export default TaskProgressWidget;

